import { ProductConfig } from "@web-solutions/core/constants/remote-config";

export enum PaymentSystem {
  STRIPE = 'stripe',
  RECURLY = 'recurly',
  SOLIDGATE = 'solidgate',
  PADDLE = 'paddle',
  PAYPAL = 'paypal',
  TAILOREDPAY = 'tailoredpay',
};

// Payment systems served by our own Foodzee backend
// (REACT_APP_API_FOODZE_BASE_URL_*) instead of the default billing API. They
// also require a billing auth token issued by that backend (source 'foodzee').
// Lives next to the enum because both `module-billing` and `core` need it and
// neither of them imports the other.
export const FOODZEE_BACKEND_SYSTEMS: string[] = [
  PaymentSystem.STRIPE,
  PaymentSystem.TAILOREDPAY,
];

export const isFoodzeeBackend = (paymentSystem?: string | null) =>
  !!paymentSystem && FOODZEE_BACKEND_SYSTEMS.includes(paymentSystem);

/** `GET /nmi/config` — bootstrap for the TailoredPay (NMI) checkout. */
export interface TailoredpayConfig {
  /** false => the gateway is not rolled out yet, fall back to paymentSystemReserve. */
  enabled: boolean;
  mode?: string;
  /** Collect.js public tokenization key. */
  tokenization_key: string;
  collect_js_url: string;
  three_ds_enabled: boolean;
  apple_pay_enabled: boolean;
  /**
   * Gateway.js (3DS) public checkout key + script URL. The brief says both keys
   * and both URLs come from /nmi/config, but the documented response shape only
   * lists the Collect.js pair — these are read optionally so 3DS lights up as
   * soon as the backend starts sending them.
   */
  gateway_js_url?: string;
  gateway_key?: string;
}

/** `POST /nmi/checkout` and `POST /nmi/one-time` response statuses. */
export enum TailoredpayStatus {
  SUCCEEDED = 'succeeded',
  DECLINED = 'declined',
  ERROR = 'error',
  PENDING = 'pending',
}

/** Payer-authentication result forwarded to the backend as `three_ds`. */
export interface TailoredpayThreeDSResult {
  cavv?: string;
  xid?: string;
  eci?: string;
  cardholder_auth?: string;
  three_ds_version?: string;
  directory_server_id?: string;
}

export interface TailoredpayTokenizeResult {
  payment_token: string;
  /** 'applePay' / 'googlePay' when the token came from a wallet, else undefined. */
  tokenType?: string;
  card?: {
    number?: string;
    bin?: string;
    exp?: string;
    hash?: string;
    type?: string;
  };
  wallet?: {
    email?: string;
    cardDetails?: string;
    cardNetwork?: string;
    billingInfo?: Record<string, string>;
    shippingInfo?: Record<string, string>;
  };
}

export enum ModeApplePay {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  ENABLED_IF_WITH_CARD = 'enabled_if_with_card',
};

export enum ModeGooglePay {
  ENABLED_IF_PAYMENT_METHOD_PRESENT = 'enabled-if-payment-method-present',
  ENABLED = 'enabled',
  DISABLED = 'disabled',
};

export enum ModeCardForm {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

export type Period = 'P1W' | 'P2W' | 'P3W' | 'P1M' | 'P2M' | 'P3M' | 'P6M' | 'P1Y' | 'ONETIME';

export type Currency = "USD" | "AUD" | "CAD" | "EUR";

export const Currencies: Array<Currency> = ["USD", "AUD", "CAD", "EUR"];

export interface Product {
  id: string,
  amount: string,
  currency: Currency,
  period: Period,
  trial_period_days: string,
  trial_price_amount: string,
  currencies: {
    country: string | string[],
    currency: Currency,
    amount: string,
    trial_price_amount: string,
  }[],
}

export interface ProductInfo extends Product, Omit<ProductConfig, 'id'> {
  trialDays: number,
  trialPrice: string,
  trialDayPrice: string,
  trialPriceAmount: number,
  prevTrialPriceAmount: number,
  prevTrialPrice: string,
  savedTrialPrice: string,
  isTrial: boolean,
  isIntroductory: boolean,
  price: string,
  priceText: string,
  priceAmount: number,
  prevPriceAmount: number,
  prevPrice: string,
  savedPrice: string,
  dayPriceAmount: number,
  dayPrice: string,
  prevDayPriceAmount: number,
  prevDayPrice: string,
  savedDayPrice: string,
  weekPriceAmount: number,
  weekPrice: string,
  prevWeekPriceAmount: number,
  prevWeekPrice: string,
  savedWeekPrice: string,
  expireDate: string,
  expireAt?: number,
  introExpireDate: string,
  isOneTimePurchase: boolean,
  vat: string,
  vatPrice: string,
  vatPriceCombo: string,
  vatTrial: string,
  vatTrialPrice: string,
  vatTrialPriceCombo: string,
  weekVat: string,
  vatWeekPrice: string,
  vatWeekPriceCombo: string,
  dayVat: string,
  vatDayPrice: string,
  vatDayPriceCombo: string,
  minutesAmount?: number,
}

export * from './prices'

export interface AlternativePaymentOrder {
  order: {
    order_id: string,
  },
}

export interface SolidgatePayPalOrder extends AlternativePaymentOrder {
  pay_form: {
    script_url: string
  }
}

export interface SolidgateMercadoPagoOrder extends AlternativePaymentOrder {
  pay_form: {
    return_url: string
  }
}
